import { create } from 'zustand';
import { produce, setAutoFreeze, type Draft } from 'immer';
import type { Body, FoliageKind, FoliageLayer, FoliagePaint, GrassLines, GrassPaint, LevelDoc, PaletteEntry, PlacedObject, SpikeLines, TerrainShape, TileLayer } from '../model/format.ts';
import type { SpikeCorner } from '../terrain/types.ts';

import { upgradeFoliagePaint, type FoliageMode, type GrassMode } from './grass.ts';
import type { LinkSlot } from './links.ts';
import type { SaveStatus } from './autosave.ts';
import { useSettings } from './settings.ts';
import { stripGeneratedTiles } from '../data/crumble.ts';

setAutoFreeze(false);

export type Tool =
  | 'select'
  | 'pan'
  | 'polygon'
  | 'tilesToPoly'
  | 'brush'
  | 'erase'
  | 'rect'
  | 'eyedropper'
  | 'object'
  | 'waypoint'
  | 'link'
  | 'detach'
  | 'grass'
  | 'spikes'
  | 'foliage'
  | 'fix';

/** What the pixel tile editor opens on: a terrain cell and its dirt, grass, or inside-corner spike tile. */
export interface TileEditRequest {
  container: string;
  x: number;
  y: number;
  layer: 'dirt' | 'grass' | 'spike';
  /** link the case (inside corners: this corner shape only), the corner with any shape around it, only cells with exactly these neighbours, or those neighbours with the same light and dark tiles */
  scope: 'exact' | 'general' | 'neighbours' | 'light';
  /** new: a new tile for the case; edit: the custom tile shown; copy: a custom copy of the tile shown */
  mode: 'new' | 'edit' | 'copy';
  /** new tiles: link alone or as another variant of the case */
  link?: 'replace' | 'add';
  /** new tiles: starting pixels (base64 RGBA) instead of the tile shown */
  start?: string;
  /** further missing cases for "Save & next" */
  queue?: { container: string; x: number; y: number; layer: 'dirt' | 'grass' | 'spike'; corner?: SpikeCorner }[];
  /** spike layer: which inside-corner dest this session draws */
  corner?: SpikeCorner;
}

export type Selection =
  | { kind: 'none' }
  | { kind: 'objects'; ids: string[] }
  | { kind: 'body'; id: string }
  | { kind: 'terrain'; id: string }
  | { kind: 'waypoint'; body: string; id: string }
  | { kind: 'cells'; rect: [number, number, number, number] };

export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export type Panel = 'layers' | 'tiles' | 'objects' | 'inspector' | 'level';

interface HistoryEntry {
  doc: LevelDoc;
  label: string;
}

export interface EditorState {
  doc: LevelDoc | null;
  /** server/local id the doc is saved under; null = unsaved import */
  savedAt: string | null;
  dirty: boolean;
  saveStatus: SaveStatus;
  past: HistoryEntry[];
  future: HistoryEntry[];

  tool: Tool;
  selection: Selection;
  /** 'level' or a body id: which container layer/terrain tools edit */
  container: string;
  activeLayer: Record<string, string>;
  brush: { entry: PaletteEntry | null; flags: number; size: number };
  objectPrefab: string | null;
  /** paint = one object per grid cell while dragging; rect = fill every cell in a dragged box */
  objectPlace: 'paint' | 'rect';
  polygon: { style: string; mode: 'add' | 'subtract'; hole: boolean };
  /** Grass brush: lines = mark single outline lines of cells instead of whole cells; join = every click marks the lines from the last one (as Shift-click) */
  grass: { mode: GrassMode; rect: boolean; join: boolean };
  /** Spike brush: same line marks as grass; slopes are skipped */
  spikes: { mode: 'paint' | 'erase'; rect: boolean; join: boolean };
  /** Foliage brush: which decoration kind it paints ('any' = all kinds) */
  foliage: { mode: FoliageMode; kind: FoliageKind | 'any'; layer: FoliageLayer | 'both'; rect: boolean; size: number };
  /** terrain cell the Fix tiles tool shows (container-local cell coordinates) */
  fixCell: null | { container: string; x: number; y: number };
  camera: Camera;
  showGrid: boolean;
  showColliders: boolean;
  showLinks: boolean;
  panel: Panel;
  panelOpen: boolean;
  /** waypoint preview scrub per body (0..1 along the path) */
  scrub: number;
  modal: null | 'open' | 'tileset' | 'help' | 'export' | 'settings' | 'history' | 'access' | 'lobby' | 'sounds';
  /** terrain style the Tileset tool opens on (null = the first one) */
  tilesetStyle: string | null;
  /** reference field the Link tool fills (from a "Pick" button); null = the selection's main activator field */
  linkSlot: LinkSlot | null;
  playing: null | { spawn?: [number, number] };
  /** "play from here": the next tap in the viewport starts play at that spot */
  pickSpawn: boolean;
  /** pixel tile editor session (null: closed) */
  tileEdit: TileEditRequest | null;
  /** the tile editor waits for a tap on a level cell to copy its tile */
  pickTile: null | ((pe: PaletteEntry, flags: number) => void);
  toast: string | null;

  setDoc(doc: LevelDoc, savedAt?: string | null): void;
  /** apply an undoable change */
  change(label: string, recipe: (d: Draft<LevelDoc>) => void): void;
  /** replace the doc without history (live drag updates); call commit() at the end */
  live(recipe: (d: Draft<LevelDoc>) => void): void;
  beginLive(label: string): void;
  commitLive(): void;
  undo(): void;
  redo(): void;
  set(p: Partial<EditorState>): void;
  notify(msg: string): void;
}

let liveBase: HistoryEntry | null = null;

/** Drop an in-progress drag without recording it. */
export function revertLive() {
  if (liveBase) useEditor.setState({ doc: liveBase.doc });
  liveBase = null;
}

export function isLive(): boolean {
  return !!liveBase;
}

/** Rebase a running drag onto changes that arrived meanwhile (lobby): `fn` maps the doc it started from. */
export function rebaseLive(fn: (doc: LevelDoc) => LevelDoc) {
  if (liveBase) liveBase = { ...liveBase, doc: fn(liveBase.doc) };
}
let toastTimer: ReturnType<typeof setTimeout> | undefined;

/** Drop objects the editor regenerates itself (crumble platform tiles; needs the game data) and read old fields into their current form. */
export function withoutDerived(doc: LevelDoc): LevelDoc {
  if (doc.foliagePaint || doc.bodies.some((b) => b.foliagePaint))
    doc = produce(doc, (d) => {
      upgradeFoliagePaint(d);
      for (const b of d.bodies) upgradeFoliagePaint(b);
    });
  try {
    return stripGeneratedTiles(doc);
  } catch {
    return doc;
  }
}

/** Focused inspector input: its live edits merge into one undo step. */
let activeInput: object | null = null;
let lastMerge: { input: object; label: string; entry: HistoryEntry } | null = null;
let activeInputHook: ((on: boolean) => void) | null = null;
let activeDrag = false;
/** `drag`: a slider held down; the lobby locks what it edits until it is let go. */
export function setActiveInput(token: object | null, drag = false) {
  activeInput = token;
  if (!token) lastMerge = null;
  const on = !!token && drag;
  if (on !== activeDrag) {
    activeDrag = on;
    activeInputHook?.(on);
  }
}

/** Told when an inspector slider drag starts and stops. */
export function onActiveInput(fn: ((on: boolean) => void) | null) {
  activeInputHook = fn;
}

export const useEditor = create<EditorState>((set, get) => ({
  doc: null,
  savedAt: null,
  dirty: false,
  saveStatus: 'saved',
  past: [],
  future: [],
  tool: 'select',
  selection: { kind: 'none' },
  container: 'level',
  activeLayer: {},
  brush: { entry: null, flags: 0, size: 1 },
  objectPrefab: null,
  objectPlace: 'paint',
  polygon: { style: '', mode: 'add', hole: false },
  grass: { mode: 'paint', rect: false, join: false },
  spikes: { mode: 'paint', rect: false, join: false },
  foliage: { mode: 'add', kind: 'any', layer: 'both', rect: false, size: 1 },
  fixCell: null,
  camera: { x: 0, y: 0, zoom: 32 },
  showGrid: useSettings.getState().showGrid,
  showColliders: false,
  showLinks: useSettings.getState().showLinks,
  panel: 'layers',
  panelOpen: globalThis.localStorage?.getItem('ple:panelOpen') !== '0',
  scrub: 0,
  modal: null,
  tilesetStyle: null,
  linkSlot: null,
  playing: null,
  pickSpawn: false,
  tileEdit: null,
  pickTile: null,
  toast: null,

  setDoc(doc, savedAt = null) {
    liveBase = null;
    set({ doc: withoutDerived(doc), savedAt, dirty: false, past: [], future: [], selection: { kind: 'none' }, container: 'level', linkSlot: null });
  },
  change(label, recipe) {
    const { doc, past } = get();
    if (!doc) return;
    const next = produce(doc, recipe);
    if (next === doc) return;
    if (activeInput && lastMerge?.input === activeInput && lastMerge.label === label && past[past.length - 1] === lastMerge.entry) {
      set({ doc: next, future: [], dirty: true });
      return;
    }
    const entry = { doc, label };
    lastMerge = activeInput ? { input: activeInput, label, entry } : null;
    set({ doc: next, past: [...past.slice(-199), entry], future: [], dirty: true });
  },
  beginLive(label) {
    const { doc } = get();
    if (doc) liveBase = { doc, label };
  },
  live(recipe) {
    const { doc } = get();
    if (!doc) return;
    set({ doc: produce(doc, recipe), dirty: true });
  },
  commitLive() {
    const { doc, past } = get();
    if (liveBase && doc && doc !== liveBase.doc) set({ past: [...past.slice(-199), liveBase], future: [] });
    liveBase = null;
  },
  undo() {
    const { past, future, doc } = get();
    const e = past[past.length - 1];
    if (!e || !doc) return;
    set({ doc: e.doc, past: past.slice(0, -1), future: [{ doc, label: e.label }, ...future], dirty: true });
    get().notify(`Undo: ${e.label}`);
  },
  redo() {
    const { past, future, doc } = get();
    const e = future[0];
    if (!e || !doc) return;
    set({ doc: e.doc, future: future.slice(1), past: [...past, { doc, label: e.label }], dirty: true });
    get().notify(`Redo: ${e.label}`);
  },
  set(p) {
    set(p);
  },
  notify(msg) {
    clearTimeout(toastTimer);
    set({ toast: msg });
    toastTimer = setTimeout(() => set({ toast: null }), 2200);
  },
}));

useEditor.subscribe((s, prev) => {
  if (s.showGrid !== prev.showGrid || s.showLinks !== prev.showLinks) useSettings.getState().update({ showGrid: s.showGrid, showLinks: s.showLinks });
  if (s.linkSlot && (s.tool !== 'link' || s.selection !== prev.selection)) useEditor.setState({ linkSlot: null });
});

// ---------- selectors / helpers ----------

/** Open the Tileset tool (on its Tile cases tab) on a terrain style: by default the selected shape's or the polygon tool's. */
export function openTileset(style?: string) {
  const s = useEditor.getState();
  if (!style && s.doc && s.selection.kind === 'terrain') {
    const id = s.selection.id;
    style = [s.doc.terrains, ...s.doc.bodies.map((b) => b.terrains)].flat().find((t) => t.id === id)?.style;
  }
  style ??= s.polygon.style || undefined;
  s.set({ modal: 'tileset', tilesetStyle: style?.split(':')[0] ?? null });
}

/** start play with the player's feet at a world position (lifted out of solid ground by the sim) */
export function playFrom(spawn?: [number, number]) {
  useEditor.getState().set({ playing: spawn ? { spawn } : {}, pickSpawn: false });
}

export interface Container {
  id: string;
  layers: TileLayer[];
  terrains: TerrainShape[];
  grassPaint?: GrassPaint;
  grassLines?: GrassLines;
  spikeLines?: SpikeLines;
  decoPaint?: FoliagePaint;
  /** world offset of the container's local tile space */
  origin: [number, number];
  rotation: number;
  body?: Body;
}

export function getContainer(doc: LevelDoc, id: string): Container {
  if (id !== 'level') {
    const b = doc.bodies.find((x) => x.id === id);
    if (b) return { id, layers: b.layers, terrains: b.terrains, grassPaint: b.grassPaint, grassLines: b.grassLines, spikeLines: b.spikeLines, decoPaint: b.decoPaint, origin: b.origin, rotation: b.rotation ?? 0, body: b };
  }
  return { id: 'level', layers: doc.layers, terrains: doc.terrains, grassPaint: doc.grassPaint, grassLines: doc.grassLines, spikeLines: doc.spikeLines, decoPaint: doc.decoPaint, origin: [0, 0], rotation: 0 };
}

export function draftContainer(d: Draft<LevelDoc>, id: string): { layers: Draft<TileLayer>[]; terrains: Draft<TerrainShape>[]; grassPaint?: GrassPaint; grassLines?: GrassLines; spikeLines?: SpikeLines; decoPaint?: FoliagePaint } {
  if (id !== 'level') {
    const b = d.bodies.find((x) => x.id === id);
    if (b) return b;
  }
  return d;
}

export function activeLayerOf(s: EditorState): TileLayer | null {
  if (!s.doc) return null;
  const c = getContainer(s.doc, s.container);
  const id = s.activeLayer[s.container];
  return c.layers.find((l) => l.id === id) ?? c.layers.find((l) => l.role === 'level') ?? c.layers[0] ?? null;
}

export function findObject(doc: LevelDoc, id: string): { obj: PlacedObject; body?: Body } | null {
  const o = doc.objects.find((x) => x.id === id);
  if (o) return { obj: o };
  for (const b of doc.bodies) {
    const bo = b.objects.find((x) => x.id === id);
    if (bo) return { obj: bo, body: b };
  }
  return null;
}

export function draftObject(d: Draft<LevelDoc>, id: string): Draft<PlacedObject> | null {
  const o = d.objects.find((x) => x.id === id);
  if (o) return o;
  for (const b of d.bodies) {
    const bo = b.objects.find((x) => x.id === id);
    if (bo) return bo;
  }
  return null;
}

export function uid(prefix: string): string {
  return prefix + Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-3);
}

export function paletteIndex(d: Draft<LevelDoc>, e: PaletteEntry): number {
  const i = d.palette.findIndex((p) => p.tex === e.tex && p.sprite === e.sprite && (p.tile ?? '') === (e.tile ?? ''));
  if (i >= 0) return i;
  d.palette.push(e.tile ? { tex: e.tex, sprite: e.sprite, tile: e.tile } : { tex: e.tex, sprite: e.sprite });
  return d.palette.length - 1;
}
